import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useOffice } from '@/hooks/useOffice';
import { useAuth } from '@/hooks/useAuth';
import OfficeSwitcher from '@/components/OfficeSwitcher';

interface NavItem {
  to: string;
  label: string;
  icon: string;
  end?: boolean;
  countKey?: string;
  adminOnly?: boolean;
  ownerOnly?: boolean;
}

const navItems: NavItem[] = [
  { to: '/', label: 'Dashboard', icon: 'ri-dashboard-line', end: true },
  { to: '/tapes', label: 'Tapes', icon: 'ri-hard-drive-2-line', countKey: 'tapes' },
  { to: '/boxes', label: 'Boxes', icon: 'ri-archive-line', countKey: 'boxes' },
  { to: '/library', label: 'Tape Library', icon: 'ri-stack-line', countKey: 'library' },
  { to: '/replacement', label: 'Tape Replacement', icon: 'ri-refresh-line', countKey: 'replacement' },
  { to: '/damaged', label: 'Scrapped Tapes', icon: 'ri-error-warning-line', countKey: 'damaged' },
  { to: '/import', label: 'Import / Export', icon: 'ri-file-excel-2-line' },
  { to: '/backup', label: 'Backup & Restore', icon: 'ri-database-2-line' },
  { to: '/logs', label: 'Activity Log', icon: 'ri-history-line', ownerOnly: true },
  { to: '/accounts', label: 'Account Management', icon: 'ri-shield-user-line', adminOnly: true },
];

export default function AppLayout() {
  const { currentOfficeId, currentOffice } = useOffice();
  const { user, profile, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const [counts, setCounts] = useState<Record<string, number>>();

  // Redirect unauthenticated visitors to the login page.
  useEffect(() => {
    if (!authLoading && !user) navigate('/login', { replace: true });
  }, [authLoading, user, navigate]);

  // Load counts for sidebar badges
  useEffect(() => {
    if (!currentOfficeId) return;
    let active = true;
    const loadCounts = async () => {
      const newCounts: Record<string, number> = {};
      try {
        const [
          tapesRes,
          boxesRes,
          libraryRes,
          replacementRes,
          damagedRes,
        ] = await Promise.all([
          supabase.from('tapes').select('id', { count: 'exact', head: true }).eq('office_id', currentOfficeId),
          supabase.from('tapes').select('box_number').eq('office_id', currentOfficeId).not('box_number', 'is', null),
          supabase.from('tapes').select('id', { count: 'exact', head: true }).eq('office_id', currentOfficeId).eq('box_number', 'Backup Tape Library'),
          supabase.from('tapes').select('id', { count: 'exact', head: true }).eq('office_id', currentOfficeId).eq('needs_replacement', true),
          supabase.from('tapes').select('id', { count: 'exact', head: true }).eq('office_id', currentOfficeId).eq('damaged', true),
        ]);
        if (!active) return;
        newCounts.tapes = tapesRes.count ?? 0;
        // Boxes are now derived from distinct tape box numbers (excluding the
        // 'Backup Tape Library' sentinel and empty values), matching the Boxes page.
        const boxSet = new Set<string>();
        ((boxesRes.data as { box_number: string | null }[]) || []).forEach((r) => {
          const bn = (r.box_number || '').trim();
          if (bn && bn !== 'Backup Tape Library') boxSet.add(bn);
        });
        newCounts.boxes = boxSet.size;
        newCounts.library = libraryRes.count ?? 0;
        newCounts.replacement = replacementRes.count ?? 0;
        newCounts.damaged = damagedRes.count ?? 0;
      } catch {
        // silently ignore count errors
      }
      if (active) setCounts(newCounts);
    };
    loadCounts();
    // Refresh counts every 60 seconds so they stay roughly current
    const interval = setInterval(loadCounts, 60000);
    return () => {
      active = false;
      clearInterval(interval);
    };
  }, [currentOfficeId]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <i className="ri-loader-4-line w-8 h-8 flex items-center justify-center animate-spin text-slate-400 text-3xl" />
      </div>
    );
  }
  if (!user) return null;

  return (
    <div className="min-h-screen flex bg-slate-50 text-slate-800" style={{ minWidth: 1024 }}>
      <aside className="w-60 shrink-0 bg-white border-r border-slate-200 flex flex-col">
        <div className="h-16 px-5 flex items-center gap-2 border-b border-slate-100">
          <div className="w-9 h-9 flex items-center justify-center rounded-md bg-slate-900 text-white">
            <i className="ri-hard-drive-3-line w-5 h-5 flex items-center justify-center" />
          </div>
          <div>
            <div className="font-semibold text-sm">TapeVault</div>
            <div className="text-[11px] text-slate-500">Backup Tape Manager</div>
          </div>
        </div>
        <div className="px-3 py-2 border-b border-slate-100">
          <OfficeSwitcher />
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems
            .filter(
              (it) =>
                (!it.adminOnly || profile?.role === 'admin') &&
                (!it.ownerOnly || profile?.is_owner === true),
            )
            .map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm cursor-pointer transition ${
                  isActive
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-600 hover:bg-slate-100'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <i className={`${it.icon} w-4 h-4 flex items-center justify-center`} />
                  <span className="whitespace-nowrap">{it.label}</span>
                  {it.countKey && counts && counts[it.countKey] > 0 && (
                    <span
                      className={`ml-auto text-[11px] font-semibold px-1.5 py-0.5 rounded-full min-w-[20px] text-center ${
                        isActive ? 'bg-white text-slate-900' : 'bg-slate-200 text-slate-700'
                      }`}
                    >
                      {counts[it.countKey]}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-100 space-y-1">
          <div className="flex items-center gap-2 px-3 py-2 text-[11px] text-slate-400">
            <i className="ri-building-2-line w-3.5 h-3.5 flex items-center justify-center" />
            <span className="truncate">{currentOffice?.name ?? '—'}</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold shrink-0">
              {(profile?.username ?? '?').charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-slate-700 truncate">{profile?.username ?? '—'}</div>
              <div className="text-[10px] text-slate-400 truncate capitalize">{profile?.role ?? ''}</div>
            </div>
            <button
              onClick={async () => {
                await signOut();
                navigate('/login', { replace: true });
              }}
              title="Sign out"
              className="w-8 h-8 flex items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 cursor-pointer"
            >
              <i className="ri-logout-box-r-line w-4 h-4 flex items-center justify-center" />
            </button>
          </div>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}